import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "@/i18n";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ClientFilterProvider } from "@/context/ClientFilterContext";
import { Toaster } from "@/components/ui/sonner";
import Login from "@/pages/Login";
import AppLayout from "@/components/AppLayout";

// Route-based code splitting: pages load on demand to keep the initial bundle small
// (faster first load, especially on mobile / slower connections).
const Register = lazy(() => import("@/pages/Register"));
const AcceptInvite = lazy(() => import("@/pages/AcceptInvite"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Projects = lazy(() => import("@/pages/Projects"));
const ProjectDetail = lazy(() => import("@/pages/ProjectDetail"));
const Variations = lazy(() => import("@/pages/Variations"));
const Invoices = lazy(() => import("@/pages/Invoices"));
const Relations = lazy(() => import("@/pages/Relations"));
const Reports = lazy(() => import("@/pages/Reports"));
const TimeTracking = lazy(() => import("@/pages/TimeTracking"));
const Weekstaat = lazy(() => import("@/pages/Weekstaat"));
const SalesInvoices = lazy(() => import("@/pages/SalesInvoices"));
const Users = lazy(() => import("@/pages/Users"));
const Settings = lazy(() => import("@/pages/Settings"));
const VakantiePlanner = lazy(() => import("@/pages/VakantiePlanner"));
const Werkrooster = lazy(() => import("@/pages/Werkrooster"));
const ClientDetail = lazy(() => import("@/pages/ClientDetail"));
const MijnPrijsafspraken = lazy(() => import("@/pages/MijnPrijsafspraken"));
const MargePerMedewerker = lazy(() => import("@/pages/MargePerMedewerker"));
const Inventory = lazy(() => import("@/pages/Inventory"));
const InventoryDetail = lazy(() => import("@/pages/InventoryDetail"));
const InventoryLabels = lazy(() => import("@/pages/InventoryLabels"));

function RouteFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center text-zinc-400 text-sm" data-testid="route-loading">
      <div className="w-5 h-5 border-2 border-zinc-300 border-t-[#0052FF] rounded-full animate-spin" />
    </div>
  );
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-8 text-slate-500">Laden…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-8 text-slate-500">Laden…</div>;
  if (user) return <Navigate to="/" replace />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <ClientFilterProvider>
        <BrowserRouter>
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
              <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
              <Route path="/uitnodiging/:token" element={<AcceptInvite />} />
              <Route path="/" element={<Protected><AppLayout /></Protected>}>
                <Route index element={<Dashboard />} />
                <Route path="projecten" element={<Projects />} />
                <Route path="projecten/:id" element={<ProjectDetail />} />
                <Route path="meerwerk" element={<Variations />} />
                <Route path="uren" element={<TimeTracking />} />
                <Route path="weekstaat" element={<Weekstaat />} />
                <Route path="verkoopfacturen" element={<SalesInvoices />} />
                <Route path="gebruikers" element={<Users />} />
                <Route path="vakantieplanner" element={<VakantiePlanner />} />
                <Route path="werkrooster" element={<Werkrooster />} />
                <Route path="inventaris" element={<Inventory />} />
                <Route path="inventaris/labels" element={<InventoryLabels />} />
                <Route path="inventaris/:id" element={<InventoryDetail />} />
                <Route path="instellingen" element={<Settings />} />
                <Route path="inkoopfacturen" element={<Invoices />} />
                <Route path="relaties" element={<Relations />} />
                <Route path="rapportages" element={<Reports />} />
                <Route path="marge" element={<MargePerMedewerker />} />
                <Route path="mijn-prijsafspraken" element={<MijnPrijsafspraken />} />
                <Route path="opdrachtgever/:id" element={<ClientDetail />} />
              </Route>
            </Routes>
          </Suspense>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </ClientFilterProvider>
    </AuthProvider>
  );
}

export default App;
